import { Checkbox, Col, Flex, Form, Row } from "antd";
import { FormWrapper, StyledButton } from "../../components/styles";
import { Description, GeneralSettingsContainer, MainFormWrapper, Title } from "./styles"
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../utils/hooks/redux";
import { fetchLanguageConfiguration, updateLanguageConfiguration } from "../../redux/features/languageConfiguration/languageConfigurationAsyncThunk";
import { selectLanguageConfiguration } from "../../redux/features/languageConfiguration/languageConfigurationSlice";
import { RequestResults } from "../../redux/common/RequestStatus";

// Available languages in the system
const AVAILABLE_LANGUAGES = [
    { code: 'en', name: 'English', required: true },
    { code: 'es', name: 'Spanish' },
    { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'ar', name: 'Arabic' },
];

interface LanguageConfigurationData {
    mainPageLanguages: string[];
}

const LanguageConfiguration: React.FC = () => {
    const [form] = Form.useForm();
    const dispatch = useAppDispatch();
    const { data, loading } = useAppSelector(selectLanguageConfiguration);

    // State to track selected languages
    const [mainPageLanguages, setMainPageLanguages] = useState<string[]>(['en']);

    const handleSave = async () => {
        try {
            const values = await form.validateFields();

            const configData: LanguageConfigurationData = {
                mainPageLanguages: values.mainPageLanguages || [],
            };

            await dispatch(updateLanguageConfiguration(configData)).unwrap();
        } catch (errorInfo) {
            console.error("Validation failed:", errorInfo);
        }
    };

    const handleMainPageLanguageChange = (checkedValues: (string | number | boolean)[]) => {
        // Always ensure English is selected
        const stringValues = checkedValues.map(v => String(v));
        if (!stringValues.includes('en')) {
            stringValues.unshift('en');
        }
        setMainPageLanguages(stringValues);
        form.setFieldsValue({ mainPageLanguages: stringValues });
    };

    useEffect(() => {
        dispatch(fetchLanguageConfiguration());
    }, [dispatch]);

    useEffect(() => {
        if (data) {
            form.setFieldsValue({
                mainPageLanguages: data.mainPageLanguages,
            });
            setMainPageLanguages(data.mainPageLanguages);
        }
    }, [data, form]);

    return (
        <>
            <GeneralSettingsContainer>
                <Title>Language Configuration</Title>
                <Description>Configure which languages are available on the main landing page. English is always available and cannot be disabled. Establishment-specific language settings are configured in each Establishment Type.</Description>

                <MainFormWrapper>
                    <FormWrapper form={form} layout="vertical">
                        <Row gutter={16}>
                            <Col span={24}>
                                <Form.Item
                                    label="Main Page Languages"
                                    name="mainPageLanguages"
                                    tooltip="Select which languages should be available on the main landing page"
                                >
                                    <Checkbox.Group
                                        value={mainPageLanguages}
                                        onChange={handleMainPageLanguageChange}
                                        style={{ width: '100%' }}
                                    >
                                        <Row gutter={[16, 16]}>
                                            {AVAILABLE_LANGUAGES.map(lang => (
                                                <Col span={24} md={8} key={lang.code}>
                                                    <Checkbox
                                                        value={lang.code}
                                                        disabled={lang.required}
                                                    >
                                                        {lang.name} {lang.required && '(Required)'}
                                                    </Checkbox>
                                                </Col>
                                            ))}
                                        </Row>
                                    </Checkbox.Group>
                                </Form.Item>
                            </Col>
                        </Row>
                    </FormWrapper>
                </MainFormWrapper>

                <Flex justify="flex-end">
                    <StyledButton
                        type="primary"
                        onClick={handleSave}
                        loading={loading === RequestResults.pending}
                    >
                        Save Language Settings
                    </StyledButton>
                </Flex>
            </GeneralSettingsContainer>
        </>
    )
}

export default LanguageConfiguration
